import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Styled from './Donation.styled';
import { CallToActionButton } from './components/CallToActionButton';
import { FrequencyButton } from './components/FrequencyButton';
import { DEFAULT_HEADING_CONTENT, DEFAULT_ONE_TIME_AMOUNTS, DEFAULT_RECURRING_AMOUNTS, DEFAULT_FREQUENCIES, DEFAULT_TAX_DEDUCTIBLE_MESSAGE } from './Donation.constants';
import { AMOUNT_INPUT, MAX_DONATION_AMOUNT, MIN_DONATION_AMOUNT } from '../../models/DonationAmount';
import { FREQUENCY } from '../../models/DonationFrequency';
import { StyledContentContainer } from '../../containers/StyledContentContainer.styled';
import { setColorWithOpacityCss } from '../../utils/setColorWithOpacityCss';
import { FrequencyDropdown } from './components/FrequencyDropdown';
import { RecurringEndDate } from './components/RecurringEndDate';
import { OtherAmount } from './components/OtherAmount';
import { AmountButton } from './components/AmountButton';
import * as SkeletonLoader from './components/SkeletonLoader';
import { createInitialState, useDonationBlockReducer } from './useDonationBlockReducer';
import { useLocale } from '../../hooks/useLocale';
import { trimAmountForCurrency } from '../../features/passport/trimAmountForCurrency';
import { useResizeFontToFit } from '../../features/passport/useResizeFontToFit';
import { useLoadingState } from '../../hooks/useLoadingState';
import { SearchSelect } from '../../molecules/SearchSelect';
import { useProgramDesignationsSearchSelect } from './useProgramDesignationsSearchSelect';
import { Check } from '../../svgs';
import { EllipsisText } from '../../styles/mixins/ellipsisText';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// only allow values of digits (optionally followed by a period and two more digits)
const isValidOtherAmountInput = value => value === '' || /^(\d+)?(\.?\d{0,2})?$/.test(value);

/**
 * The `Donation` content block allows donors to donate to the campaign.
 */
export const Donation = _ref => {
  let {
    cssBackgroundColor,
    cssBackgroundColorOpacity,
    alignXPreset,
    headingContent = DEFAULT_HEADING_CONTENT,
    taxDeductibleMessage = DEFAULT_TAX_DEDUCTIBLE_MESSAGE,
    cssTaxDeductibleColor,
    donationAmounts = DEFAULT_ONE_TIME_AMOUNTS,
    recurringAmounts = DEFAULT_RECURRING_AMOUNTS,
    donationFrequencies = DEFAULT_FREQUENCIES,
    hideRecurringEndDate = true,
    parentLayoutInfo,
    isLoading,
    onInitialization,
    orgId = '',
    campaignId = '',
    isCartEnabled = false,
    dcfEnabled = false,
    minDonationAmount: rawMinDonationAmount = MIN_DONATION_AMOUNT,
    maxDonationAmount = MAX_DONATION_AMOUNT,
    defaultProgramDesignation,
    checkoutBaseUrl,
    forwardCheckoutQueryParams,
    currencyCode = 'USD',
    showProgramDesignationsSearch = false,
    initialProgramDesignations = [],
    showAmountPlaceholder = false,
    showRecurringNudgeTag = false,
    fetchProgramDesignations = async () => [],
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation(['blocks.donation', 'blocks.common']);
  const componentId = React.useId();
  const {
    loadingState,
    resetLoadingStateOnAnimationEnd
  } = useLoadingState(isLoading);
  const frequencies = donationFrequencies.filter(frequency => frequency.isVisible);
  const recurringFrequencies = frequencies.filter(frequency => frequency.name !== FREQUENCY.ONE_TIME);
  const recurringFrequencyOptions = recurringFrequencies.flatMap(_ref2 => {
    let {
      name
    } = _ref2;
    return name;
  });
  const userLocale = useLocale();
  const [blockState, dispatch] = useDonationBlockReducer({
    donationFrequencies,
    donationAmounts,
    recurringAmounts,
    currencyCode,
    userLocale
  });

  // A reducer's initial state cannot be changed after creation and must be manually dispatched
  React.useEffect(() => {
    dispatch({
      type: 'SET_ENTIRE_BLOCK_STATE',
      newBlockState: createInitialState({
        donationFrequencies,
        donationAmounts,
        recurringAmounts,
        currencyCode,
        userLocale
      })
    });
  }, [donationFrequencies, donationAmounts, recurringAmounts, currencyCode, userLocale, dispatch]);

  /**
   * Convert min donation amount to match the locale's precision (prevents comparision errors
   * when comparing the other amount to the min donation amount)
   */
  const minDonationAmount = trimAmountForCurrency(rawMinDonationAmount, currencyCode, userLocale);
  React.useEffect(() => {
    if (!userLocale) return;
    dispatch({
      type: 'SET_USER_LOCALE',
      userLocale
    });
  }, [userLocale, dispatch]);
  React.useEffect(() => {
    if (!currencyCode) return;
    dispatch({
      type: 'SET_CURRENCY_CODE',
      currencyCode
    });
  }, [currencyCode, dispatch]);
  const visibleDonationAmounts = donationAmounts.filter(amount => amount.isVisible);
  const visibleRecurringAmounts = recurringAmounts.filter(amount => amount.isVisible);
  const amounts = blockState.frequency === FREQUENCY.ONE_TIME ? visibleDonationAmounts : visibleRecurringAmounts;
  const isOneBigButton = amounts.length === 1 && amounts[0].name.startsWith('preset');
  React.useEffect(() => {
    /**
     * Send initialization callback when the block is done loading.
     * For example, Whammy uses this to send a heap event (See DonationController)
     *
     * relevant ticket: CL-36844
     */
    if (!onInitialization || isLoading) return;
    const {
      amount,
      frequency,
      otherAmountValue
    } = blockState;
    onInitialization({
      amount,
      campaignId,
      currencyCode,
      dcfEnabled,
      frequency,
      isCartEnabled,
      orgId,
      otherAmountValue,
      visibleDonationAmounts,
      visibleRecurringAmounts
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
  const containerRef = React.useRef(null);
  const externalDependency = React.useMemo(() => [blockState.frequency, blockState.currencyCode], [blockState.frequency, blockState.currencyCode]);
  useResizeFontToFit({
    containerRef,
    elementClassName: 'donation-amount-button',
    // Keep in sync with Styled.AmountButton
    defaultFontSize: 20,
    externalDependency
  });

  // Program designations search
  const {
    options: programDesignationSearchOptions,
    value: programDesignationSearchValue,
    setValue: setProgramDesignationSearchValue,
    inputValue: programDesignationSearchInputValue,
    setInputValue: setProgramDesignationSearchInputValue,
    isLoading: isProgramDesignationSearchLoading,
    hasMoreThanOneProgramDesignation
  } = useProgramDesignationsSearchSelect({
    initialOptions: initialProgramDesignations,
    selectedOption: defaultProgramDesignation,
    onSearch: fetchProgramDesignations
  });
  return /*#__PURE__*/_jsx(StyledContentContainer, {
    "data-id": "cp-donation-form",
    parentLayoutInfo: parentLayoutInfo,
    onAnimationEnd: resetLoadingStateOnAnimationEnd,
    children: /*#__PURE__*/_jsxs(Styled.DonationContainer, {
      cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
      alignXPreset: alignXPreset,
      parentLayoutInfo: parentLayoutInfo,
      ...rest,
      children: [/*#__PURE__*/_jsxs("form", {
        onSubmit: ev => ev.preventDefault(),
        children: [/*#__PURE__*/_jsx("h3", {
          children: headingContent
        }), frequencies.length > 1 && frequencies.find(frequency => frequency.name === FREQUENCY.ONE_TIME) && (loadingState === 'loading' ? /*#__PURE__*/_jsx(SkeletonLoader.FrequencyButtonFieldset, {}) : /*#__PURE__*/_jsxs(Styled.FrequencyButtonFieldset, {
          "aria-label": t('Donation.frequency.fieldset.ariaLabel', 'Donation frequency'),
          children: [/*#__PURE__*/_jsx(FrequencyButton, {
            doFadeIn: loadingState === 'loaded',
            "data-id": "cp-donation-form-one-time",
            isActive: blockState.frequency === FREQUENCY.ONE_TIME,
            handleClick: () => {
              dispatch({
                type: 'SELECT_FREQUENCY_BUTTON',
                presets: donationAmounts,
                frequency: FREQUENCY.ONE_TIME
              });
            },
            children: t('blocks.common:Frequency.one-time', 'One-time')
          }), /*#__PURE__*/_jsx(FrequencyButton, {
            "data-id": "cp-donation-form-recurring",
            doFadeIn: loadingState === 'loaded',
            isActive: blockState.frequency !== FREQUENCY.ONE_TIME,
            showRecurringNudgeTag: showRecurringNudgeTag,
            handleClick: () => {
              dispatch({
                type: 'SELECT_FREQUENCY_BUTTON',
                presets: recurringAmounts,
                frequency: recurringFrequencyOptions[0]
              });
            },
            children: recurringFrequencyOptions.length > 1 ? t('Donation.frequency.recurring', 'Recurring') : t(`blocks.common:Frequency.${recurringFrequencyOptions[0]}`, recurringFrequencyOptions[0])
          })]
        })), /*#__PURE__*/_jsx(Styled.ChooseAmount, {
          children: /*#__PURE__*/_jsx(Trans, {
            t: t,
            i18nKey: "Donation.chooseAmount.label",
            values: {
              frequency: blockState.frequency
            },
            defaults: "Choose an amount to donate <b>{{frequency}}</b>",
            components: {
              b: /*#__PURE__*/_jsx("b", {})
            }
          })
        }), blockState.frequency !== FREQUENCY.ONE_TIME && (recurringFrequencyOptions.length > 1 || !hideRecurringEndDate) && /*#__PURE__*/_jsxs(Styled.FrequencyDropdownFieldset, {
          "aria-label": t('Donation.frequencyDropdownFieldset.ariaLabel', 'Recurring donation frequency and/or optional end date'),
          disabled: loadingState === 'loading',
          children: [recurringFrequencyOptions.length > 1 && /*#__PURE__*/_jsx(FrequencyDropdown, {
            frequencySelectValue: blockState.frequency,
            recurringFrequencyOptions: recurringFrequencyOptions,
            handleSelectChange: frequency => {
              dispatch({
                type: 'SELECT_DONATION_FREQUENCY',
                frequency
              });
            }
          }), !hideRecurringEndDate && /*#__PURE__*/_jsx(RecurringEndDate, {
            handleDatePickerChange: recurringEndDate => {
              dispatch({
                type: 'SELECT_RECURRING_END_DATE',
                recurringEndDate
              });
            }
          })]
        }), /*#__PURE__*/_jsxs(Styled.DonationAmountFieldset, {
          ref: containerRef,
          "aria-label": t('Donation.amountFieldset.ariaLabel', 'Donation amount'),
          children: [loadingState === 'loading' && /*#__PURE__*/_jsx(SkeletonLoader.Fieldset, {}), loadingState !== 'loading' && amounts.map((preset, index) => preset.name.startsWith('preset') ? /*#__PURE__*/_jsx(AmountButton, {
            showAmountPlaceholder: showAmountPlaceholder,
            dataId: blockState.frequency === FREQUENCY.ONE_TIME ? `cp-donation-form-onetime-preset-${index + 1}` : `cp-donation-form-recurring-preset-${index + 1}`,
            activeInput: blockState.activeInput,
            currencyCode: blockState.currencyCode,
            frequency: blockState.frequency,
            isOneBigButton: isOneBigButton,
            presetAmount: preset.amount,
            presetName: preset.name,
            doFadeIn: loadingState === 'loaded',
            userLocale: blockState.userLocale,
            handleClick: () => {
              dispatch({
                type: 'SELECT_PRESET_AMOUNT_BUTTON',
                selectedPreset: preset
              });
            }
          }, preset.name) : preset.name === AMOUNT_INPUT.OTHER && /*#__PURE__*/_jsx(OtherAmount, {
            dataId: blockState.frequency === FREQUENCY.ONE_TIME ? `cp-donation-form-onetime-custom` : `cp-donation-form-recurring-custom`,
            currencyCode: blockState.currencyCode,
            currencySymbol: blockState.currencySymbol,
            error: blockState.error,
            doFadeIn: loadingState === 'loaded',
            isActive: blockState.activeInput === AMOUNT_INPUT.OTHER,
            otherAmountDisplayValue: blockState.otherAmountDisplayValue,
            formHasPresets: amounts.some(amount => amount.name !== AMOUNT_INPUT.OTHER),
            handleOtherInputBlur: () => {
              dispatch({
                type: 'SET_OTHER_AMOUNT_DISPLAY_VALUE'
              });
            },
            handleOtherInputChange: e => {
              const value = e.target.value.replace(/,/g, '');
              if (!isValidOtherAmountInput(value)) {
                return;
              }
              dispatch({
                type: 'SET_OTHER_AMOUNT',
                amount: value,
                minDonationAmount,
                maxDonationAmount
              });
            }
          }, preset.name))]
        }), isCartEnabled && showProgramDesignationsSearch && hasMoreThanOneProgramDesignation && /*#__PURE__*/_jsxs(Styled.SelectProgramDesignation, {
          children: [/*#__PURE__*/_jsx("label", {
            htmlFor: `${componentId}-select-program-designation`,
            children: t('Donation.programDesignation.label', "I'd like to support")
          }), isLoading ? /*#__PURE__*/_jsx(SkeletonLoader.ProgramDesignationsSearch, {}) : /*#__PURE__*/_jsx(Styled.SearchSelectContainer, {
            doFadeIn: loadingState === 'loaded',
            children: /*#__PURE__*/_jsx(SearchSelect, {
              inputProps: {
                id: `${componentId}-select-program-designation`
              },
              options: programDesignationSearchOptions,
              value: programDesignationSearchValue,
              onChange: setProgramDesignationSearchValue,
              inputValue: programDesignationSearchInputValue,
              onInputChange: setProgramDesignationSearchInputValue,
              isLoading: isProgramDesignationSearchLoading,
              renderOption: (option, isSelectedOption) => /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(EllipsisText, {
                  children: option.label
                }), isSelectedOption && /*#__PURE__*/_jsx(Check, {
                  style: {
                    marginInlineStart: 'auto'
                  }
                })]
              })
            })
          })]
        }), /*#__PURE__*/_jsx(Styled.Divider, {}), /*#__PURE__*/_jsx(CallToActionButton, {
          blockState: blockState,
          isLoading: loadingState === 'loading',
          campaignId: campaignId,
          orgId: orgId,
          error: !!blockState.error,
          isCartEnabled: isCartEnabled,
          programDesignation: programDesignationSearchValue ? {
            id: Number(programDesignationSearchValue.id),
            name: programDesignationSearchValue.label
          } : defaultProgramDesignation,
          forwardCheckoutQueryParams: forwardCheckoutQueryParams,
          checkoutBaseUrl: checkoutBaseUrl
        })]
      }), /*#__PURE__*/_jsx(Styled.TaxDeductibleMessage, {
        cssTaxDeductibleColor: cssTaxDeductibleColor,
        children: taxDeductibleMessage
      })]
    })
  });
};